import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@y42/environment';
import { ErrorHandlerService } from '@y42/error-handling';
import { map, Observable, ObservableInput, ObservedValueOf, OperatorFunction, throwError } from 'rxjs';
import {
  ErrorResponseHttpModel,
  ChangePasswordHttpModel,
  UserHttpModel,
  UserLanguage,
  UserNotificationsHttpModel,
  UserUIModel,
} from '../models';

@Injectable({ providedIn: 'root' })
export class UserHttpService {
  private readonly baseUrl = `${environment.url.identity_api}`;

  constructor(private readonly httpClient: HttpClient, private readonly errorHandlerService: ErrorHandlerService) {}

  deleteActiveUser(): Observable<void> {
    return this.httpClient.delete<void>(`${environment.url.identity_api}/users/me`).pipe(map(() => undefined));
  }

  updateActiveUser(user: Partial<UserUIModel>): Observable<UserHttpModel> {
    return this.httpClient
      .put<{ data: UserHttpModel }>(`${this.baseUrl}/user/profile`, user)
      .pipe(map(({ data }) => data));
  }

  getActiveUser(): Observable<UserHttpModel> {
    return this.httpClient.get<{ data: UserHttpModel }>(`${this.baseUrl}/user/profile`).pipe(map(({ data }) => data));
  }

  updateEmailSubscriptions(userNotifications: UserNotificationsHttpModel): Observable<UserHttpModel> {
    return this.httpClient
      .put<{ data: UserHttpModel }>(`${this.baseUrl}/user/email/subscriptions`, userNotifications)
      .pipe(map(({ data }) => data));
  }

  changePassword(changePassword: ChangePasswordHttpModel): Observable<void> {
    return this.httpClient.put(`${this.baseUrl}/user/password`, changePassword).pipe(
      map(() => undefined),
      this.catchErrorAndThrow('[UserService.changePassword]: could not change password'),
    );
  }

  resendVerificationEmail(): Observable<void> {
    return this.httpClient.post<unknown>(`${this.baseUrl}/user/send/verify/email`, {}).pipe(map(() => undefined));
  }

  joinOrg(orgId: number): Observable<void> {
    return this.httpClient
      .post<unknown>(`${environment.url.identity_api}/join/${orgId}`, null)
      .pipe(map(() => undefined));
  }

  leaveOrg(orgId: number): Observable<void> {
    return this.httpClient
      .delete<unknown>(`${environment.url.identity_api}/company/${orgId}/user/membership`)
      .pipe(map(() => undefined));
  }

  private catchErrorAndThrow<T, O extends ObservableInput<never>>(
    message: string,
  ): OperatorFunction<T, T | ObservedValueOf<O>> {
    return this.errorHandlerService.catchError(message, (error: unknown) =>
      throwError(() => {
        if (error instanceof HttpErrorResponse) {
          return new Error((error.error as ErrorResponseHttpModel)?.message || '');
        }
        return error;
      }),
    );
  }

  getLanguages(): Observable<UserLanguage[]> {
    const url = 'assets/languages/language-names.json';

    return this.httpClient.get<Record<string, [string, string]>>(url).pipe(
      map((response) =>
        Object.entries(response).map(([isoCode, [nameLocalized, name]]) => {
          const userLanguage: UserLanguage = {
            isoCode,
            name,
            nameLocalized,
          };
          return userLanguage;
        }),
      ),
    );
  }
}
